import { z } from 'zod';
import { AnlagenTyp, MangelSchwere, MangelVorlageQuelle } from '../enums.js';
/**
 * Erfassung vor Ort — Fotos, Textbibliothek und KI-Vorschlag.
 *
 * Die Schrittfolge aus dem Prozessbild des Auftraggebers: Foto → Stichworte →
 * KI-Text → Bibliothekstreffer → Vorschrift → Maßnahme. Alles hier ist
 * Human-in-the-Loop: Kein Vorschlag landet ohne Klick im Mangel.
 *
 * Die Fotos selbst sind seit der Einführung von `Medium` gewöhnliche Medien
 * mit dem Zweck `mangel_detail` (schemas/medium.ts). Hier bleibt nur die
 * fachliche Grenze, wie viele davon an EINEN Befund gehören.
 */
// ------------------------------------------------------------ Fotos am Mangel
/**
 * Mehr Fotos je Mangel braucht niemand — ein Befund ist ein Befund. Die Grenze
 * hält den Bericht lesbar und den Speicher überschaubar; durchgesetzt wird sie
 * beim Upload eines Mediums mit Bezug auf einen Mangel.
 */
export const MANGEL_FOTO_MAX_ANZAHL = 10;
// ------------------------------------------------------------- Textbibliothek
/**
 * Vorlage — ein einmal gut formulierter Befund, der wiederkommt.
 *
 * Der abgerissene Feuerlöscherhalter, der verstellte Fluchtweg, das fehlende
 * Rettungszeichen: Diese Texte schreibt niemand zweimal gern. `verwendungen`
 * zählt, wie oft die Vorlage in einen Mangel übernommen wurde — das sortiert
 * die Treffer und zeigt, welche Vorlagen taugen.
 */
export const mangelVorlage = z.object({
    id: z.number().int().positive(),
    titel: z.string(),
    beschreibung: z.string(),
    massnahme: z.string().nullable().optional(),
    /** Vorschrift oder Norm — Freitext, fachlich verantwortet vom Ersteller. */
    grundlage: z.string().nullable().optional(),
    schwere: MangelSchwere,
    /** Gesetzt, wenn die Vorlage nur zu einer Anlagenart passt. */
    anlagenTyp: AnlagenTyp.nullable().optional(),
    verwendungen: z.number().int().nonnegative(),
    quelle: MangelVorlageQuelle,
    aktiv: z.boolean(),
    erstelltVonId: z.number().int().positive().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const createMangelVorlageRequest = mangelVorlage
    .omit({
    id: true,
    verwendungen: true,
    quelle: true,
    aktiv: true,
    erstelltVonId: true,
    createdAt: true,
    updatedAt: true,
})
    .extend({
    titel: z.string().trim().min(3).max(200),
    beschreibung: z.string().trim().min(3).max(5000),
    massnahme: z.string().trim().max(5000).nullable().optional(),
    grundlage: z.string().trim().max(500).nullable().optional(),
});
/** `aktiv` ist beim Ändern dabei — sonst ließe sich eine deaktivierte Vorlage nie zurückholen. */
export const updateMangelVorlageRequest = createMangelVorlageRequest
    .extend({ aktiv: z.boolean() })
    .partial();
export const mangelVorlageListQuery = z.object({
    /** Volltext über Titel und Beschreibung. */
    q: z
        .string()
        .trim()
        .max(200)
        .optional()
        .transform((v) => (v ? v : undefined)),
    anlagenTyp: AnlagenTyp.optional(),
    /** `true` = auch deaktivierte zeigen. Der Normalfall ist die aktive Bibliothek. */
    inaktive: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => v === 'true'),
    limit: z.coerce.number().int().positive().max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
});
// ----------------------------------------------------------------- Vorschlag
/** Höchstens so viele Bibliothekstreffer je Anfrage — mehr liest niemand vor Ort. */
export const MANGEL_VORSCHLAG_MAX_VORLAGEN = 3;
export const mangelVorschlagRequest = z.object({
    /** Freie Stichworte vom Rundgang: „Feuerlöscher Halterung abgerissen Flur EG". */
    stichworte: z.string().trim().min(3).max(1000),
    anlagenTyp: AnlagenTyp.optional(),
    liegenschaftId: z.number().int().positive().optional(),
});
/**
 * Was die KI vorschlägt. Alles vier ist ein ENTWURF — die Grundlage ganz
 * besonders: Eine Fundstelle aus dem Sprachmodell ist eine Vermutung, keine
 * Recherche. Die Oberfläche kennzeichnet sie als „Vorschlag, bitte prüfen".
 */
export const mangelVorschlag = z.object({
    beschreibung: z.string().trim().min(1).max(5000),
    massnahme: z.string().trim().max(5000).default(''),
    grundlage: z.string().trim().max(500).default(''),
    schwere: MangelSchwere,
});
export const mangelVorschlagResponse = z.object({
    /** `null`, wenn keine KI konfiguriert ist oder der Aufruf scheiterte. */
    vorschlag: mangelVorschlag.nullable(),
    /** Die passendsten Vorlagen aus der Bibliothek — höchstens drei. */
    vorlagen: z.array(mangelVorlage).max(MANGEL_VORSCHLAG_MAX_VORLAGEN),
    kiVerfuegbar: z.boolean(),
    /** Warum kein Vorschlag kam, in ganzen Sätzen für die Oberfläche. */
    hinweis: z.string().optional(),
});
